/**
 * Pure rule resolver — no React, no Firestore.
 *
 * Walks the `hourlyRates` rules list and returns the applicable amount
 * for a given (section, program, clientIsInternal) context. An explicit
 * `override` (manual €/h) bypasses the whole matching and wins.
 */
import type {
  DesignationSection,
  HourlyRate,
  ProgramType,
} from '@altyor-kronos/shared-types';

export interface RateResolutionContext {
  section: DesignationSection;
  program: ProgramType;
  clientIsInternal: boolean;
}

export type RateResolutionSource = 'override' | 'rule' | 'default' | 'none';

export interface RateResolution {
  amount: number;
  source: RateResolutionSource;
  rule?: HourlyRate;
}

function ruleMatches(rule: HourlyRate, ctx: RateResolutionContext): boolean {
  if (rule.matchSection != null && rule.matchSection !== ctx.section) return false;
  if (rule.matchProgram != null && rule.matchProgram !== ctx.program) return false;
  if (rule.matchClientType === 'internal' && !ctx.clientIsInternal) return false;
  return true;
}

function hasSpecificCriteria(rule: HourlyRate): boolean {
  return (
    rule.matchSection != null ||
    rule.matchProgram != null ||
    rule.matchClientType != null
  );
}

function cmp(a: HourlyRate, b: HourlyRate): number {
  // priority desc, then label asc for determinism
  if (a.priority !== b.priority) return b.priority - a.priority;
  return a.label.localeCompare(b.label);
}

export function resolveHourlyRate(
  ctx: RateResolutionContext,
  rules: readonly HourlyRate[],
  override?: number,
): RateResolution {
  if (override != null && Number.isFinite(override) && override > 0) {
    return { amount: override, source: 'override' };
  }

  // Specific rules: have at least one match criterion and match the context
  const specific = rules.filter((r) => hasSpecificCriteria(r) && ruleMatches(r, ctx));
  if (specific.length > 0) {
    const [top] = [...specific].sort(cmp);
    return { amount: top.amount, source: 'rule', rule: top };
  }

  // Default rules: isDefault with no specific criteria (wildcards), must also pass ruleMatches
  const defaults = rules.filter(
    (r) => r.isDefault && !hasSpecificCriteria(r) && ruleMatches(r, ctx),
  );
  if (defaults.length > 0) {
    const [top] = [...defaults].sort(cmp);
    return { amount: top.amount, source: 'default', rule: top };
  }

  return { amount: 0, source: 'none' };
}
